import React from "react";
import {Helmet} from "react-helmet-async";
import {useLocation} from "react-router-dom";

const DEFAULT_DESCRIPTION = "UCC specializes in designing and implementing hardware, software, and other cutting-edge technological solutions in the public infrastructure domain.";
const DEFAULT_IMAGE = "/images/static/seo-image.jpg";
const DEFAULT_IMAGE_ALT = "Explore UCC Infratech solutions";
const DEFAULT_SITE_NAME = "UCC Infratech";
const FALLBACK_OG_TITLE = "The United Construction Company Limited";
const FALLBACK_OG_DESCRIPTION = "The Infratech Division of UCC designs and implements hardware, software, and other cutting-edge technological solutions through our wide network of partners, spread across the globe.";

const ORGANIZATION_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "The United Construction Company Limited",
    "url": "https://uccbd.com/infratech-division/",
    "logo": "https://uccbd.com/ucc-logo-white.svg",
    "description": "The Infratech Division of UCC designs & implements hardware, software and other cutting-edge technological solutions through our wide network of partners, spread across the globe. With a vast experience in the public sector, we ensure seamless implementation and execution of a diverse range of projects.",
    "email": "info@uccbd.com",
    "telephone": "+88 02 48814283-4",
    "address": {
        "@type": "PostalAddress",
        "streetAddress": "House: 83, Baridhara Diplomatic Zone, Baridhara",
        "addressLocality": "Dhaka",
        "postalCode": "1212",
        "addressCountry": "Bangladesh"
    },
    "openingHours": "Mo-Su 10:00-19:30",
    "areaServed": "Bangladesh",
    "knowsAbout": [
        "Construction",
        "Infrastructure",
        "Technology Solutions",
        "IT Solutions"
    ]
};

const toAbsoluteUrl = (value, origin) => {
    if (!value) {
        return "";
    }

    if (/^https?:\/\//i.test(value)) {
        return value;
    }

    return `${origin}${value.startsWith("/") ? value : `/${value}`}`;
};

const Seo = ({
    title,
    description,
    image,
    type = "website",
    siteName = DEFAULT_SITE_NAME,
    noIndex = false,
}) => {
    const location = useLocation();
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const currentUrl = origin ? `${origin}${location.pathname}${location.search}` : "";
    const seoTitle = title || siteName;
    const seoDescription = description || DEFAULT_DESCRIPTION;
    const seoImage = toAbsoluteUrl(image || DEFAULT_IMAGE, origin);

    return (
        <Helmet>
            <meta charSet="utf-8"/>
            <title>{seoTitle}</title>
            <meta name="description" content={seoDescription}/>
            <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"}/>
            {currentUrl && <link rel="canonical" href={currentUrl}/>}

            <meta property="og:type" content={type}/>
            <meta property="og:site_name" content={siteName}/>
            <meta property="og:title" content={seoTitle || FALLBACK_OG_TITLE}/>
            <meta property="og:description" content={seoDescription || FALLBACK_OG_DESCRIPTION}/>
            {currentUrl && <meta property="og:url" content={currentUrl}/>}
            {seoImage && <meta property="og:image" content={seoImage}/>}
            {seoImage && <meta property="og:image:width" content="1200"/>}
            {seoImage && <meta property="og:image:height" content="630"/>}
            {seoImage && <meta property="og:image:alt" content={DEFAULT_IMAGE_ALT}/>}

            <meta name="twitter:card" content="summary_large_image"/>
            <meta name="twitter:title" content={seoTitle}/>
            <meta name="twitter:description" content={seoDescription}/>
            {seoImage && <meta name="twitter:image" content={seoImage}/>}
            {seoImage && <meta name="twitter:image:alt" content={DEFAULT_IMAGE_ALT}/>}

            <script type="application/ld+json">{JSON.stringify(ORGANIZATION_SCHEMA)}</script>
        </Helmet>
    );
};

export default Seo;

